import type { ModelIdType } from "@app/types/assistant/models/types";
import { ioTsEnum } from "@app/types/shared/utils/iots_utils";
import { z } from "zod";

import {
  CLAUDE_3_5_HAIKU_20241022_MODEL_ID,
  CLAUDE_3_5_SONNET_20240620_MODEL_ID,
  CLAUDE_3_5_SONNET_20241022_MODEL_ID,
  CLAUDE_3_7_SONNET_20250219_MODEL_ID,
  CLAUDE_3_HAIKU_20240307_MODEL_ID,
  CLAUDE_3_OPUS_2024029_MODEL_ID,
  CLAUDE_4_5_HAIKU_20251001_MODEL_ID,
  CLAUDE_4_5_HAIKU_DEFAULT_MODEL_CONFIG,
  CLAUDE_4_5_OPUS_20251101_MODEL_ID,
  CLAUDE_4_5_SONNET_20250929_MODEL_ID,
  CLAUDE_4_OPUS_20250514_MODEL_ID,
  CLAUDE_4_SONNET_20250514_MODEL_ID,
  CLAUDE_FABLE_5_DEFAULT_MODEL_CONFIG,
  CLAUDE_FABLE_5_MODEL_ID,
  CLAUDE_OPUS_4_6_DEFAULT_MODEL_CONFIG,
  CLAUDE_OPUS_4_6_MODEL_ID,
  CLAUDE_OPUS_4_7_DEFAULT_MODEL_CONFIG,
  CLAUDE_OPUS_4_7_MODEL_ID,
  CLAUDE_OPUS_4_8_DEFAULT_MODEL_CONFIG,
  CLAUDE_OPUS_4_8_MODEL_ID,
  CLAUDE_OPUS_5_DEFAULT_MODEL_CONFIG,
  CLAUDE_OPUS_5_MODEL_ID,
  CLAUDE_SONNET_4_6_DEFAULT_MODEL_CONFIG,
  CLAUDE_SONNET_4_6_MODEL_ID,
  CLAUDE_SONNET_5_DEFAULT_MODEL_CONFIG,
  CLAUDE_SONNET_5_MODEL_ID,
} from "./anthropic";
import {
  AUTO_COMPLEX_MODEL_CONFIG,
  AUTO_COMPLEX_MODEL_ID,
  AUTO_FAST_MODEL_CONFIG,
  AUTO_FAST_MODEL_ID,
  AUTO_MODEL_CONFIG,
  AUTO_MODEL_ID,
} from "./auto";
// Custom models (generated at build time from GCS, empty in dev).
import {
  CUSTOM_MODEL_CONFIGS,
  CUSTOM_MODEL_IDS,
} from "./custom_models.generated";
import { DEEPSEEK_CHAT_MODEL_ID } from "./deepseek";
import {
  FIREWORKS_DEEPSEEK_V3P2_MODEL_ID,
  FIREWORKS_DEEPSEEK_V4_FLASH_0731_MODEL_CONFIG,
  FIREWORKS_DEEPSEEK_V4_FLASH_0731_MODEL_ID,
  FIREWORKS_DEEPSEEK_V4_PRO_0813_MODEL_CONFIG,
  FIREWORKS_DEEPSEEK_V4_PRO_0813_MODEL_ID,
  FIREWORKS_DEEPSEEK_V4_PRO_MODEL_CONFIG,
  FIREWORKS_DEEPSEEK_V4_PRO_MODEL_ID,
  FIREWORKS_GLM_5_MODEL_ID,
  FIREWORKS_GLM_5P2_MODEL_CONFIG,
  FIREWORKS_GLM_5P2_MODEL_ID,
  FIREWORKS_GLM_5P3_FLASH_MODEL_CONFIG,
  FIREWORKS_GLM_5P3_FLASH_MODEL_ID,
  FIREWORKS_INKLING_MODEL_CONFIG,
  FIREWORKS_INKLING_MODEL_ID,
  FIREWORKS_KIMI_K2_INSTRUCT_MODEL_ID,
  FIREWORKS_KIMI_K2P5_MODEL_ID,
  FIREWORKS_KIMI_K2P6_MODEL_CONFIG,
  FIREWORKS_KIMI_K2P6_MODEL_ID,
  FIREWORKS_KIMI_K3_MODEL_CONFIG,
  FIREWORKS_KIMI_K3_MODEL_ID,
  FIREWORKS_MINIMAX_M2P5_MODEL_ID,
} from "./fireworks";
import {
  GEMINI_2_5_FLASH_LITE_MODEL_ID,
  GEMINI_2_5_FLASH_MODEL_ID,
  GEMINI_2_5_PRO_MODEL_ID,
  GEMINI_3_1_FLASH_IMAGE_MODEL_ID,
  GEMINI_3_1_FLASH_LITE_MODEL_CONFIG,
  GEMINI_3_1_FLASH_LITE_MODEL_ID,
  GEMINI_3_1_FLASH_LITE_PREVIEW_DEPRECATED_MODEL_ID,
  GEMINI_3_1_PRO_MODEL_CONFIG,
  GEMINI_3_1_PRO_MODEL_ID,
  GEMINI_3_5_FLASH_LITE_MODEL_CONFIG,
  GEMINI_3_5_FLASH_LITE_MODEL_ID,
  GEMINI_3_5_FLASH_MODEL_CONFIG,
  GEMINI_3_5_FLASH_MODEL_ID,
  GEMINI_3_6_FLASH_MODEL_CONFIG,
  GEMINI_3_6_FLASH_MODEL_ID,
  GEMINI_3_7_FLASH_MODEL_CONFIG,
  GEMINI_3_7_FLASH_MODEL_ID,
  GEMINI_3_8_FLASH_MODEL_CONFIG,
  GEMINI_3_8_FLASH_MODEL_ID,
  GEMINI_3_FLASH_MODEL_ID,
  GEMINI_3_PRO_IMAGE_MODEL_ID,
  GEMINI_3_PRO_MODEL_ID,
} from "./google_ai_studio";
import {
  MISTRAL_CODESTRAL_MODEL_CONFIG,
  MISTRAL_CODESTRAL_MODEL_ID,
  MISTRAL_LARGE_MODEL_CONFIG,
  MISTRAL_LARGE_MODEL_ID,
  MISTRAL_MEDIUM_3_5_MODEL_CONFIG,
  MISTRAL_MEDIUM_3_5_MODEL_ID,
  MISTRAL_MEDIUM_MODEL_ID,
  MISTRAL_SMALL_MODEL_CONFIG,
  MISTRAL_SMALL_MODEL_ID,
} from "./mistral";
import { NOOP_MODEL_CONFIG, NOOP_MODEL_ID } from "./noop";
import {
  GPT_3_5_TURBO_MODEL_ID,
  GPT_4_1_MINI_MODEL_ID,
  GPT_4_1_MODEL_ID,
  GPT_4_TURBO_MODEL_ID,
  GPT_4O_20240806_MODEL_ID,
  GPT_4O_MINI_MODEL_ID,
  GPT_4O_MODEL_ID,
  GPT_5_1_MODEL_CONFIG,
  GPT_5_1_MODEL_ID,
  GPT_5_2_MODEL_CONFIG,
  GPT_5_2_MODEL_ID,
  GPT_5_4_MINI_MODEL_CONFIG,
  GPT_5_4_MINI_MODEL_ID,
  GPT_5_4_MODEL_CONFIG,
  GPT_5_4_MODEL_ID,
  GPT_5_4_NANO_MODEL_CONFIG,
  GPT_5_4_NANO_MODEL_ID,
  GPT_5_5_MODEL_CONFIG,
  GPT_5_5_MODEL_ID,
  GPT_5_6_LUNA_MODEL_CONFIG,
  GPT_5_6_LUNA_MODEL_ID,
  GPT_5_6_SOL_MODEL_CONFIG,
  GPT_5_6_SOL_MODEL_ID,
  GPT_5_6_TERRA_LONG_CONTEXT_MODEL_CONFIG,
  GPT_5_6_TERRA_LONG_CONTEXT_MODEL_ID,
  GPT_5_6_TERRA_MODEL_CONFIG,
  GPT_5_6_TERRA_MODEL_ID,
  GPT_5_MINI_MODEL_CONFIG,
  GPT_5_MINI_MODEL_ID,
  GPT_5_MODEL_CONFIG,
  GPT_5_MODEL_ID,
  GPT_5_NANO_MODEL_CONFIG,
  GPT_5_NANO_MODEL_ID,
  GPT_6_ASTRA_MODEL_CONFIG,
  GPT_6_ASTRA_MODEL_ID,
  GPT_IMAGE_1_5_MODEL_ID,
  GPT_IMAGE_2_MODEL_ID,
  O1_MINI_MODEL_ID,
  O1_MODEL_ID,
  O3_MINI_MODEL_ID,
  O3_MODEL_ID,
  O4_MINI_MODEL_ID,
} from "./openai";
import type { ModelConfigurationType } from "./types";
import {
  GROK_3_MINI_MODEL_ID,
  GROK_3_MODEL_ID,
  GROK_4_1_FAST_NON_REASONING_MODEL_ID,
  GROK_4_1_FAST_REASONING_MODEL_ID,
  GROK_4_5_MODEL_CONFIG,
  GROK_4_5_MODEL_ID,
  GROK_4_6_MODEL_CONFIG,
  GROK_4_6_MODEL_ID,
  GROK_4_FAST_NON_REASONING_MODEL_ID,
  GROK_4_FAST_REASONING_MODEL_ID,
  GROK_4_MODEL_ID,
} from "./xai";

// Static model IDs (compile-time known, used for pricing maps).
export const STATIC_MODEL_IDS = [
  GPT_3_5_TURBO_MODEL_ID,
  GPT_4_TURBO_MODEL_ID,
  GPT_4O_MODEL_ID,
  GPT_4_1_MODEL_ID,
  GPT_4_1_MINI_MODEL_ID,
  GPT_4O_20240806_MODEL_ID,
  GPT_4O_MINI_MODEL_ID,
  GPT_5_1_MODEL_ID,
  GPT_5_2_MODEL_ID,
  GPT_5_4_MINI_MODEL_ID,
  GPT_5_4_MODEL_ID,
  GPT_5_5_MODEL_ID,
  GPT_6_ASTRA_MODEL_ID,
  GPT_5_6_SOL_MODEL_ID,
  GPT_5_6_TERRA_MODEL_ID,
  GPT_5_6_TERRA_LONG_CONTEXT_MODEL_ID,
  GPT_5_6_LUNA_MODEL_ID,
  GPT_5_4_NANO_MODEL_ID,
  GPT_5_MODEL_ID,
  GPT_5_MINI_MODEL_ID,
  GPT_5_NANO_MODEL_ID,
  O1_MODEL_ID,
  O1_MINI_MODEL_ID,
  O3_MODEL_ID,
  O3_MINI_MODEL_ID,
  O4_MINI_MODEL_ID,
  CLAUDE_4_OPUS_20250514_MODEL_ID,
  CLAUDE_4_SONNET_20250514_MODEL_ID,
  CLAUDE_4_5_SONNET_20250929_MODEL_ID,
  CLAUDE_4_5_OPUS_20251101_MODEL_ID,
  CLAUDE_OPUS_4_6_MODEL_ID,
  CLAUDE_OPUS_4_7_MODEL_ID,
  CLAUDE_OPUS_4_8_MODEL_ID,
  CLAUDE_OPUS_5_MODEL_ID,
  CLAUDE_FABLE_5_MODEL_ID,
  CLAUDE_SONNET_5_MODEL_ID,
  CLAUDE_SONNET_4_6_MODEL_ID,
  CLAUDE_3_OPUS_2024029_MODEL_ID,
  CLAUDE_3_5_SONNET_20240620_MODEL_ID,
  CLAUDE_3_5_SONNET_20241022_MODEL_ID,
  CLAUDE_3_7_SONNET_20250219_MODEL_ID,
  CLAUDE_3_HAIKU_20240307_MODEL_ID,
  CLAUDE_3_5_HAIKU_20241022_MODEL_ID,
  CLAUDE_4_5_HAIKU_20251001_MODEL_ID,
  MISTRAL_LARGE_MODEL_ID,
  MISTRAL_MEDIUM_MODEL_ID,
  MISTRAL_MEDIUM_3_5_MODEL_ID,
  MISTRAL_SMALL_MODEL_ID,
  MISTRAL_CODESTRAL_MODEL_ID,
  GEMINI_2_5_FLASH_MODEL_ID,
  GEMINI_2_5_FLASH_LITE_MODEL_ID,
  GEMINI_2_5_PRO_MODEL_ID,
  GEMINI_3_PRO_MODEL_ID,
  GEMINI_3_1_FLASH_LITE_MODEL_ID,
  GEMINI_3_1_FLASH_LITE_PREVIEW_DEPRECATED_MODEL_ID,
  GEMINI_3_1_PRO_MODEL_ID,
  GEMINI_3_FLASH_MODEL_ID,
  GEMINI_3_5_FLASH_LITE_MODEL_ID,
  GEMINI_3_5_FLASH_MODEL_ID,
  GEMINI_3_6_FLASH_MODEL_ID,
  GEMINI_3_7_FLASH_MODEL_ID,
  GEMINI_3_8_FLASH_MODEL_ID,
  DEEPSEEK_CHAT_MODEL_ID,
  FIREWORKS_DEEPSEEK_V3P2_MODEL_ID,
  FIREWORKS_DEEPSEEK_V4_FLASH_0731_MODEL_ID,
  FIREWORKS_DEEPSEEK_V4_PRO_MODEL_ID,
  FIREWORKS_DEEPSEEK_V4_PRO_0813_MODEL_ID,
  FIREWORKS_KIMI_K2_INSTRUCT_MODEL_ID,
  FIREWORKS_KIMI_K2P5_MODEL_ID,
  FIREWORKS_KIMI_K2P6_MODEL_ID,
  FIREWORKS_KIMI_K3_MODEL_ID,
  FIREWORKS_MINIMAX_M2P5_MODEL_ID,
  FIREWORKS_GLM_5_MODEL_ID,
  FIREWORKS_GLM_5P2_MODEL_ID,
  FIREWORKS_GLM_5P3_FLASH_MODEL_ID,
  FIREWORKS_INKLING_MODEL_ID,
  GROK_3_MODEL_ID,
  GROK_3_MINI_MODEL_ID,
  GROK_4_5_MODEL_ID,
  GROK_4_6_MODEL_ID,
  GROK_4_MODEL_ID,
  GROK_4_FAST_REASONING_MODEL_ID,
  GROK_4_FAST_NON_REASONING_MODEL_ID,
  GROK_4_1_FAST_NON_REASONING_MODEL_ID,
  GROK_4_1_FAST_REASONING_MODEL_ID,
  NOOP_MODEL_ID,
  AUTO_MODEL_ID,
  AUTO_FAST_MODEL_ID,
  AUTO_COMPLEX_MODEL_ID,
] as const;

// Type for static model IDs only (excludes custom models from GCS).
export type StaticModelIdType = (typeof STATIC_MODEL_IDS)[number];

export const isStaticModelId = (
  modelId: ModelIdType
): modelId is StaticModelIdType =>
  STATIC_MODEL_IDS.includes(modelId as StaticModelIdType);

// Combined model IDs: static + custom (custom models generated at build time from GCS).
export const MODEL_IDS = [...STATIC_MODEL_IDS, ...CUSTOM_MODEL_IDS] as const;

export const isModelId = (modelId: string): modelId is ModelIdType =>
  MODEL_IDS.includes(modelId as ModelIdType);

export const ModelIdCodec = ioTsEnum<(typeof MODEL_IDS)[number]>(MODEL_IDS);

// Note: MODEL_IDS includes dynamic custom models from GCS, so we use z.custom
// with the isModelId guard rather than z.enum (which requires a static tuple).
export const ModelIdSchema = z.custom<ModelIdType>(
  (val) => typeof val === "string" && isModelId(val),
  { message: "Invalid model ID" }
);

// Image generation model IDs (internal-only, not user-selectable)
export const IMAGE_MODEL_IDS = [
  GEMINI_3_PRO_IMAGE_MODEL_ID,
  GEMINI_3_1_FLASH_IMAGE_MODEL_ID,
  GPT_IMAGE_1_5_MODEL_ID,
  GPT_IMAGE_2_MODEL_ID,
] as const;

export type ImageModelIdType = (typeof IMAGE_MODEL_IDS)[number];
export const SUPPORTED_MODEL_CONFIGS: ModelConfigurationType[] = [
  GPT_5_1_MODEL_CONFIG,
  GPT_5_2_MODEL_CONFIG,
  GPT_5_4_MINI_MODEL_CONFIG,
  GPT_5_4_MODEL_CONFIG,
  GPT_5_5_MODEL_CONFIG,
  GPT_6_ASTRA_MODEL_CONFIG,
  GPT_5_6_SOL_MODEL_CONFIG,
  GPT_5_6_TERRA_MODEL_CONFIG,
  GPT_5_6_TERRA_LONG_CONTEXT_MODEL_CONFIG,
  GPT_5_6_LUNA_MODEL_CONFIG,
  GPT_5_4_NANO_MODEL_CONFIG,
  GPT_5_MODEL_CONFIG,
  GPT_5_MINI_MODEL_CONFIG,
  GPT_5_NANO_MODEL_CONFIG,
  CLAUDE_OPUS_4_6_DEFAULT_MODEL_CONFIG,
  CLAUDE_OPUS_4_7_DEFAULT_MODEL_CONFIG,
  CLAUDE_OPUS_4_8_DEFAULT_MODEL_CONFIG,
  CLAUDE_OPUS_5_DEFAULT_MODEL_CONFIG,
  CLAUDE_FABLE_5_DEFAULT_MODEL_CONFIG,
  CLAUDE_SONNET_5_DEFAULT_MODEL_CONFIG,
  CLAUDE_SONNET_4_6_DEFAULT_MODEL_CONFIG,
  CLAUDE_4_5_HAIKU_DEFAULT_MODEL_CONFIG,
  MISTRAL_LARGE_MODEL_CONFIG,
  MISTRAL_MEDIUM_3_5_MODEL_CONFIG,
  MISTRAL_SMALL_MODEL_CONFIG,
  MISTRAL_CODESTRAL_MODEL_CONFIG,
  GEMINI_3_1_FLASH_LITE_MODEL_CONFIG,
  GEMINI_3_1_PRO_MODEL_CONFIG,
  GEMINI_3_5_FLASH_LITE_MODEL_CONFIG,
  GEMINI_3_5_FLASH_MODEL_CONFIG,
  GEMINI_3_6_FLASH_MODEL_CONFIG,
  GEMINI_3_7_FLASH_MODEL_CONFIG,
  GEMINI_3_8_FLASH_MODEL_CONFIG,
  FIREWORKS_DEEPSEEK_V4_FLASH_0731_MODEL_CONFIG,
  FIREWORKS_DEEPSEEK_V4_PRO_MODEL_CONFIG,
  FIREWORKS_DEEPSEEK_V4_PRO_0813_MODEL_CONFIG,
  FIREWORKS_KIMI_K2P6_MODEL_CONFIG,
  FIREWORKS_KIMI_K3_MODEL_CONFIG,
  FIREWORKS_GLM_5P2_MODEL_CONFIG,
  FIREWORKS_GLM_5P3_FLASH_MODEL_CONFIG,
  FIREWORKS_INKLING_MODEL_CONFIG,
  GROK_4_5_MODEL_CONFIG,
  GROK_4_6_MODEL_CONFIG,
  NOOP_MODEL_CONFIG,
  AUTO_MODEL_CONFIG,
  AUTO_FAST_MODEL_CONFIG,
  AUTO_COMPLEX_MODEL_CONFIG,
  // Custom models (generated at build time from GCS).
  ...CUSTOM_MODEL_CONFIGS,
];
export default SUPPORTED_MODEL_CONFIGS;
